import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Text } from './Texte';
import type { LucideIcon } from './icons';
import { IconPill } from './IconPill';
import type { AccentKey } from '../theme/accents';
import { colors, radius, spacing, typography } from '../theme/tokens';
import { fleche } from '../i18n/sens';

interface SettingsRowProps {
  label: string;
  value?: string;
  onPress: () => void;
  variant?: 'default' | 'danger';
  showChevron?: boolean;
  // Pastille d'icône douce optionnelle à gauche (style Instagram/Meta).
  icon?: LucideIcon;
  accent?: AccentKey;
  testID?: string;
}

// Ligne de réglage style iOS/Meta : (pastille d'icône) + libellé à gauche, valeur/chevron à droite.
export function SettingsRow({
  label,
  value,
  onPress,
  variant = 'default',
  showChevron = true,
  icon,
  accent = 'blue',
  testID,
}: SettingsRowProps) {
  const danger = variant === 'danger';
  return (
    <Pressable testID={testID} accessibilityRole="button" onPress={onPress} style={styles.row}>
      <View style={styles.left}>
        {icon ? <IconPill icon={icon} accent={accent} variant="soft" size={32} /> : null}
        <Text style={[styles.label, danger && styles.danger]}>{label}</Text>
      </View>
      <View style={styles.right}>
        {value ? <Text style={styles.value}>{value}</Text> : null}
        {showChevron && !danger ? <Text style={styles.chevron}>{fleche('›')}</Text> : null}
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    backgroundColor: colors.white,
    borderRadius: radius.md,
    minHeight: 52,
  },
  left: { flexDirection: 'row', alignItems: 'center', gap: spacing.md, flexShrink: 1 },
  label: { fontSize: typography.body.size, color: colors.grey900 },
  danger: { color: colors.error, fontWeight: typography.bodyMd.weight },
  right: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  value: { fontSize: typography.body.size, color: colors.grey500 },
  chevron: { fontSize: typography.h3.size, color: colors.grey400 },
});
